import React, { useMemo, useState } from 'react';
import { CheckCircle2Icon, MousePointerClickIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { lotes } from '../data/lotes';
import { formatQ } from '../utils/format';
import type { Lote } from '../types/lote';
const COLS = 8;
const ROWS = 5;
const LOT_W = 66;
const LOT_H = 90;
const GAP = 8;
const PAD = 34;
const ROAD = 36;
const ROAD_AFTER_ROW = 1; // calle horizontal después de la segunda fila

const VIEW_W = PAD * 2 + COLS * LOT_W + (COLS - 1) * GAP;
const VIEW_H = PAD * 2 + ROWS * LOT_H + (ROWS - 1) * GAP + ROAD;
const fills: Record<Lote['estado'], {
  fill: string;
  stroke: string;
  text: string;
  label: string;
}> = {
  disponible: {
    fill: '#336856',
    stroke: '#1a3c2e',
    text: '#ffffff',
    label: 'Disponible'
  },
  vendido: {
    fill: '#a8332c',
    stroke: '#7d241f',
    text: '#ffffff',
    label: 'Vendido'
  },
  apartado: {
    fill: '#d3a01f',
    stroke: '#a3760a',
    text: '#12291f',
    label: 'Apartado'
  }
};
interface LotMapProps {
  onApartar: (lote: Lote) => void;
}
export function LotMap({
  onApartar
}: LotMapProps) {
  const [hovered, setHovered] = useState<{
    lote: Lote;
    x: number;
    y: number;
  } | null>(null);
  const [selected, setSelected] = useState<Lote | null>(null);
  const positions = useMemo(() => lotes.map((lote, index) => {
    const col = index % COLS;
    const row = Math.floor(index / COLS);
    return {
      lote,
      x: PAD + col * (LOT_W + GAP),
      y: PAD + row * (LOT_H + GAP) + (row > ROAD_AFTER_ROW ? ROAD : 0)
    };
  }), []);
  const disponibles = lotes.filter(l => l.estado === 'disponible').length;
  return <section id="lotes" className="bg-white py-20 lg:py-28" data-id="element-196" data-mp="1wrg7sn1umuhb">
      <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-197" data-mp="1rsc0y32ed8u5">
        <SectionHeading title="Plano de lotificación" description={`40 lotes en total, ${disponibles} aún disponibles. Pase el cursor sobre un lote para ver medidas y precio, y haga clic para apartarlo.`} data-id="element-198" data-mp="1rqsfly1vw7li" />

        <div className="mt-14 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start" data-id="element-199" data-mp="n3hsaj23amp1">
          {/* Plano SVG interactivo */}
          <div className="relative rounded-sm bg-sand p-3 sm:p-5" onMouseLeave={() => setHovered(null)} data-id="element-200" data-mp="r5csfk8aau18">
            <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="h-auto w-full" role="group" aria-label="Plano interactivo de lotes" data-id="element-201" data-mp="w60q2or0q94f">
              {/* Calles */}
              <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="#eceee9" rx="2" data-id="element-202" data-mp="1a2nqj1i6jhf3" />
              <rect x="0" y={PAD + (ROAD_AFTER_ROW + 1) * (LOT_H + GAP) - GAP} width={VIEW_W} height={ROAD} fill="#d8dbd4" data-id="element-203" data-mp="1euffj7lotf34" />
              <text x={PAD} y={PAD + (ROAD_AFTER_ROW + 1) * (LOT_H + GAP) - GAP + 23} fill="#12291f" opacity="0.45" fontSize="13" letterSpacing="3" fontFamily="Montserrat, sans-serif" data-id="element-204" data-mp="1ot4t4o6jwddk">
                CALLE PRINCIPAL
              </text>

              {positions.map(({
              lote,
              x,
              y
            }) => {
              const style = fills[lote.estado];
              const isSelected = selected?.numero === lote.numero;
              const isAvailable = lote.estado === 'disponible';
              return <g key={lote.numero} tabIndex={0} role="button" aria-label={`Lote ${lote.numero}, ${lote.medidas}, ${formatQ(lote.precio)}, ${style.label}`} className={isAvailable ? 'cursor-pointer outline-none' : 'cursor-not-allowed outline-none'} onMouseEnter={() => setHovered({
                lote,
                x: x + LOT_W / 2,
                y
              })} onFocus={() => setHovered({
                lote,
                x: x + LOT_W / 2,
                y
              })} onBlur={() => setHovered(null)} onClick={() => isAvailable && setSelected(lote)} onKeyDown={event => {
                if ((event.key === 'Enter' || event.key === ' ') && isAvailable) {
                  event.preventDefault();
                  setSelected(lote);
                }
              }} data-id="element-205" data-mp="u16aqq9bz77">
                    <rect x={x} y={y} width={LOT_W} height={LOT_H} rx="2" fill={style.fill} stroke={isSelected ? '#ffffff' : style.stroke} strokeWidth={isSelected ? 4 : 1} opacity={lote.estado === 'vendido' ? 0.62 : 1} className="transition-opacity duration-150 ease-smooth hover:opacity-90" data-id="element-206" data-mp="3qkghw0rl5n4k" />
                    <text x={x + LOT_W / 2} y={y + LOT_H / 2 + 5} textAnchor="middle" fill={style.text} fontSize="17" fontWeight="600" fontFamily="Montserrat, sans-serif" pointerEvents="none" data-id="element-207" data-mp="15sxycdztxr6i">
                      {lote.numero}
                    </text>
                  </g>;
            })}
            </svg>

            {/* Tooltip */}
            {hovered && <div className="pointer-events-none absolute z-10 w-44 -translate-x-1/2 -translate-y-full rounded-sm bg-forest-900 px-4 py-3 text-white shadow-card" style={{
            left: `calc(${hovered.x / VIEW_W * 100}% )`,
            top: `calc(${hovered.y / VIEW_H * 100}% - 6px)`
          }} role="status" data-id="element-208" data-mp="22kun49f9p6ag">
                <p className="font-display text-base" data-id="element-209" data-mp="3nyh5q9cpn4dw">Lote {hovered.lote.numero}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.1em] text-white/70" data-id="element-210" data-mp="3o0jxoyxfb7l9">
                  {hovered.lote.medidas} · {hovered.lote.area} m²
                </p>
                <p className="mt-2 text-[15px] font-semibold text-gold-400" data-id="element-211" data-mp="2pkjqdsa18ovj">
                  {formatQ(hovered.lote.precio)}
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.1em] text-white/70" data-id="element-212" data-mp="2ynkvpqr2qby5">
                  {fills[hovered.lote.estado].label}
                </p>
              </div>}
          </div>

          {/* Leyenda + lote seleccionado */}
          <div className="space-y-6" data-id="element-213" data-mp="26tsjcxdipakb">
            <div className="rounded-sm border border-forest-900/10 p-6" data-id="element-214" data-mp="2awymhw5c2lmk">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-215" data-mp="2avnh5rw8p5tc">
                Leyenda
              </p>
              <ul className="mt-4 space-y-3" data-id="element-216" data-mp="1j73yc2t7p1zh">
                {(Object.keys(fills) as Lote['estado'][]).map(estado => <li key={estado} className="flex items-center gap-3 text-[13px] text-forest-900/80" data-id="element-217" data-mp="1n6l12oh97l0y">
                    <span className="h-4 w-6 rounded-sm" style={{
                  backgroundColor: fills[estado].fill
                }} aria-hidden="true" data-id="element-218" data-mp="1fnv1foo8v4eu" />
                    {fills[estado].label}
                    <span className="ml-auto text-forest-900/50" data-id="element-219" data-mp="11ovlr6iplzut">
                      {lotes.filter(l => l.estado === estado).length}
                    </span>
                  </li>)}
              </ul>
            </div>

            {selected ? <div className="rounded-sm bg-forest-900 p-6 text-white" data-id="element-220" data-mp="rwpp3jhiiu9a">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-400" data-id="element-221" data-mp="n08xugf0nzrz">
                  Lote seleccionado
                </p>
                <p className="mt-2 font-display text-3xl" data-id="element-222" data-mp="dyrix1dci44v">Lote {selected.numero}</p>
                <dl className="mt-5 space-y-2.5 text-[13px]" data-id="element-223" data-mp="8zy517u5cb46">
                  <div className="flex justify-between border-b border-white/15 pb-2" data-id="element-224" data-mp="90g8opj51rm4">
                    <dt className="text-white/65" data-id="element-225" data-mp="1wxxlln40dsqm">Medidas</dt>
                    <dd data-id="element-226" data-mp="21w8wm161i9af">{selected.medidas}</dd>
                  </div>
                  <div className="flex justify-between border-b border-white/15 pb-2" data-id="element-227" data-mp="21tcyocu6zklz">
                    <dt className="text-white/65" data-id="element-228" data-mp="1ny26563f7bks">Área</dt>
                    <dd data-id="element-229" data-mp="1stzmvdilijy3">{selected.area} m²</dd>
                  </div>
                  <div className="flex justify-between border-b border-white/15 pb-2" data-id="element-230" data-mp="1e6ekjltjoor8">
                    <dt className="text-white/65" data-id="element-231" data-mp="1iwuxz5xd8adl">Precio</dt>
                    <dd className="font-semibold text-gold-400" data-id="element-232" data-mp="1iyftoyvf1zd1">{formatQ(selected.precio)}</dd>
                  </div>
                  <div className="flex justify-between" data-id="element-233" data-mp="153detm8lczo3">
                    <dt className="text-white/65" data-id="element-234" data-mp="1983b3c75wg9j">Enganche desde</dt>
                    <dd data-id="element-235" data-mp="v7k5ebq65maj">{formatQ(10000)}</dd>
                  </div>
                </dl>
                <button type="button" onClick={() => onApartar(selected)} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-[background-color,transform] duration-150 ease-smooth hover:bg-gold-600 active:scale-[0.98]" data-id="element-236" data-mp="v9mvswv8hhfs">
                  <CheckCircle2Icon size={16} data-id="element-237" data-mp="ljy1l3q0c2zg" />
                  Apartar este lote
                </button>
              </div> : <div className="flex items-start gap-3 rounded-sm bg-sand p-6 text-[13px] leading-relaxed text-forest-900/70" data-id="element-238" data-mp="zi4qhuczasmz">
                <MousePointerClickIcon size={18} className="mt-0.5 shrink-0 text-gold" data-id="element-239" data-mp="ui9zzrx4852g" />
                Seleccione un lote verde en el plano para ver su ficha completa y apartarlo con
                Q10,000.
              </div>}
          </div>
        </div>
      </div>
    </section>;
}